import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { useTemplateEditorState } from '../context/UseTemplateEditorContext';
import { TEMPLATE_EDITOR_FAKE_CC_ID, TEMPLATE_EDITOR_FAKE_MACHINEGUID } from '../constants';
import { useMutation } from 'react-query';
import { offsitePreviewTemplate, onsitePreviewTemplate } from '../utils/queries';
import useNotifications from 'core/notifications/useNotifications';
import {
  IOffsiteTemplatePreviewPayload,
  OnsiteTemplatePreviewPayload,
} from 'sections/design/template-management/entities/templates';
import { TemplateType } from 'models/template/Template';

const useOnsiteStrategy = () => {
  const { t } = useTranslation('designs');
  const mutation = useMutation(onsitePreviewTemplate);

  const { template, compilationContext } = useTemplateEditorState();
  const { showNotification, closeNotification } = useNotifications();

  const previewTemplate = useCallback(() => {
    const precompiledTemplate = template?.template ?? '';

    const payload: OnsiteTemplatePreviewPayload = {
      siteId: template.siteId,
      template: precompiledTemplate,
      compilationContext: compilationContext.compilationContext,
    };

    const onSuccess = () => {
      closeNotification();
    };

    const onError = (error: Error) => {
      if (error.cause !== undefined && error.cause !== null) {
        const details = typeof error.cause === 'string' ? error.cause : JSON.stringify(error.cause, null, 2);
        showNotification(error.message, 'error', 5000, details);
      } else {
        showNotification(error.message, 'error');
      }
    };

    mutation.mutate({ payload, t }, { onSuccess, onError });
  }, [
    closeNotification,
    compilationContext.compilationContext,
    mutation,
    showNotification,
    t,
    template?.template,
    template.siteId,
  ]);

  return {
    data: mutation.data,
    isSuccessful: mutation.isSuccess,
    previewTemplate,
  };
};

const useOffsiteStrategy = () => {
  const { t } = useTranslation('designs');
  const { showNotification, closeNotification } = useNotifications();

  const { siteId, language } = useParams<{
    siteId: string;
    id: string;
    channel: string;
    language: string;
  }>();

  const mutation = useMutation(offsitePreviewTemplate);

  const { template, linkLookups, compilationContext } = useTemplateEditorState();

  const previewTemplate = useCallback(() => {
    const precompiledTemplate = template?.template ?? '';

    const payload: IOffsiteTemplatePreviewPayload = {
      template: {
        type: 'body',
        value: precompiledTemplate,
      },
      channel: template.channel,
      featureType: template.type,
      bulkFeatureType: template.type === TemplateType.BulkRetargeting ? TemplateType.CartAbandonment : undefined,
      linkConfig: linkLookups,
      compilationContext: compilationContext.compilationContext,
      language,
      ids: {
        siteId: siteId,
        compilationId: compilationContext.snapshotId ?? TEMPLATE_EDITOR_FAKE_CC_ID,
        snapshotId: compilationContext.snapshotId ?? TEMPLATE_EDITOR_FAKE_CC_ID,
        machineGuid: compilationContext.compilationContext.machineGuid ?? TEMPLATE_EDITOR_FAKE_MACHINEGUID,
      },
      lookbackPeriod: 1440, // 24 hours in minutes
    };

    const onSuccess = () => {
      closeNotification();
    };

    const onError = (error: Error) => {
      if (error.cause !== undefined && error.cause !== null) {
        const details = typeof error.cause === 'string' ? error.cause : JSON.stringify(error.cause, null, 2);
        showNotification(error.message, 'error', 5000, details);
      } else {
        showNotification(error.message, 'error');
      }
    };

    mutation.mutate({ payload, t }, { onError, onSuccess });
  }, [
    closeNotification,
    compilationContext.compilationContext,
    compilationContext.snapshotId,
    language,
    linkLookups,
    mutation,
    showNotification,
    siteId,
    t,
    template.channel,
    template?.template,
    template.type,
  ]);

  return {
    data: mutation.data,
    isSuccessful: mutation.isSuccess,
    previewTemplate,
  };
};

export const usePreviewTemplate = () => {
  const { pathname } = useLocation();
  const isOnsiteTemplate = pathname.includes('onsite');

  const onsitePreview = useOnsiteStrategy();
  const offsitePreview = useOffsiteStrategy();

  if (isOnsiteTemplate) {
    return onsitePreview;
  } else {
    return offsitePreview;
  }
};
